import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { ArrowRight, Palette, Video, Sun } from 'lucide-react';

const JeremyLipkingPaintingsPage = () => {
    return (
        <div data-testid="jeremy-lipking-paintings-page">
            <SEO
                title="Jeremy Lipking Painting Workshop — Outdoor Figure & Landscape"
                description="Watch Jeremy Lipking paint the outdoor figure in this rare workshop recording. Learn his approach to natural light, limited palette, and California landscape painting at Zarolla."
                url="/jeremy-lipking-paintings"
                keywords={['Jeremy Lipking', 'Jeremy Lipking painting', 'Jeremy Lipking workshop', 'outdoor figure painting', 'plein air figure painting']}
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'Course',
                    '@id': 'https://zarolla.com/jeremy-lipking-paintings#course',
                    url: 'https://zarolla.com/jeremy-lipking-paintings',
                    name: 'Jeremy Lipking Painting Workshop — Outdoor Figure & Landscape',
                    description: 'A rare workshop recording of Jeremy Lipking painting the outdoor figure. Learn his approach to natural light, the limited palette, and integrating figures with landscape.',
                    provider: {
                        '@type': 'Organization',
                        name: 'Zarolla Online Art Academy',
                        url: 'https://zarolla.com',
                    },
                    instructor: {
                        '@type': 'Person',
                        name: 'Jeremy Lipking',
                        jobTitle: 'Fine Artist',
                    },
                    hasCourseInstance: {
                        '@type': 'CourseInstance',
                        courseMode: 'online',
                    },
                    isPartOf: { '@id': 'https://zarolla.com/#website' },
                    datePublished: '2007-01-01',
                    dateModified: new Date().toISOString().split('T')[0],
                    inLanguage: 'en-US',
                }}
            />

            <div className="section-gap">
                <div className="container-main">
                    {/* Hero */}
                    <div className="max-w-3xl mb-16">
                        <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                            Master artist workshop
                        </span>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-primary leading-tight mt-4">
                            Jeremy Lipking: Painting the Outdoor Figure
                        </h1>
                        <p className="text-muted-foreground text-lg mt-6 max-w-2xl">
                            One of the most celebrated figurative painters working today, captured on video painting the figure outdoors in natural California light. A rare workshop recording available exclusively at Zarolla.
                        </p>
                        <div className="flex flex-wrap gap-4 mt-8">
                            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 text-base px-8 py-6" data-testid="lipking-view-workshop-btn">
                                <Link to="/workshops">
                                    View Workshop Details <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                            </Button>
                            <Button asChild variant="outline" size="lg" className="text-base px-8 py-6" data-testid="lipking-about-btn">
                                <Link to="/instructor/jeremy-lipking">
                                    About Jeremy Lipking
                                </Link>
                            </Button>
                        </div>
                    </div>

                    {/* Section 1 */}
                    <section className="py-12 border-t border-border" data-testid="lipking-section-1">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                            <div>
                                <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                                    Why artists study Lipking
                                </h2>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    Jeremy Lipking's paintings evoke the nineteenth-century Naturalist movement while being entirely contemporary. His cool, limited palette and masterful handling of outdoor light have earned him recognition at the highest levels of contemporary figurative art &mdash; from the California Art Club to major gallery exhibitions nationwide.
                                </p>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    A former student of <Link to="/glen-orbik-drawing-course" className="text-accent hover:underline">Glen Orbik</Link>, Lipking combines classical draftsmanship with a poetic sensitivity to atmosphere that is uniquely his own. His work demonstrates that rigorous training and personal expression are not opposites &mdash; they are partners.
                                </p>
                                <p className="text-muted-foreground leading-relaxed">
                                    Watching Lipking work is a masterclass in seeing. He simplifies where other painters fuss, finds color where others see gray, and finishes with an authority that comes only from deep experience and clear thinking.
                                </p>
                            </div>
                            <div className="bg-secondary/30 rounded-sm p-8">
                                <h3 className="font-medium text-primary mb-4">Workshop highlights:</h3>
                                <ul className="space-y-3 text-muted-foreground">
                                    <li className="flex items-start gap-3">
                                        <Sun className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                                        <span>Painting the figure in natural outdoor light &mdash; handling changing conditions</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Palette className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                                        <span>Limited palette approach for color harmony and atmospheric unity</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Video className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                                        <span>Complete painting session from setup to finished work</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1 flex-shrink-0">&mdash;</span>
                                        <span>Hear Lipking explain his thought process as he works</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Section 2 */}
                    <section className="py-12 border-t border-border" data-testid="lipking-section-2">
                        <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                            What makes Lipking's approach distinctive
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div>
                                <h3 className="font-medium text-primary mb-3">Cool light, warm shadow</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Lipking's signature color temperature relationships create an atmospheric quality that sets his work apart. Learn how he manages warm and cool notes to unify the figure with its environment &mdash; a skill few painters master.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-medium text-primary mb-3">Economy of means</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Fewer brushstrokes, not more. Lipking demonstrates that restraint &mdash; knowing what to leave out &mdash; is what gives a painting authority. This workshop shows how simplification strengthens every element.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-medium text-primary mb-3">Figure in landscape</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Integrating the human figure with a natural setting is one of painting's great challenges. Lipking solves it by treating figure and environment as a single light problem &mdash; an approach grounded in his classical training under Orbik.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Section 3: Lineage */}
                    <section className="py-12 border-t border-border" data-testid="lipking-section-3">
                        <div className="max-w-3xl">
                            <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                                From the studio of Glen Orbik
                            </h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                Lipking's technical foundation was built under <Link to="/glen-orbik-drawing-course" className="text-accent hover:underline">Glen Orbik</Link>, who taught the Frank Reilly method of head and figure construction. That structural training &mdash; the ability to draw accurately before painting &mdash; underlies even Lipking's most atmospheric work. His paintings never sacrifice form for effect.
                            </p>
                            <p className="text-muted-foreground leading-relaxed mb-6">
                                Alongside <Link to="/tony-pro-painting-course" className="text-accent hover:underline">Tony Pro</Link>, Lipking represents the next generation of the Reilly tradition: artists who inherited rigorous classical training and applied it to deeply personal subjects. Studying their work alongside Orbik's teaching reveals how foundational principles generate diverse artistic outcomes.
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <Button asChild variant="outline" size="sm">
                                    <Link to="/methods/alla-prima-painting">
                                        Read: Alla Prima Painting Guide
                                    </Link>
                                </Button>
                                <Button asChild variant="outline" size="sm">
                                    <Link to="/instructor/jeremy-lipking">
                                        Full Biography
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </section>

                    {/* CTA */}
                    <section className="py-12 border-t border-border" data-testid="lipking-section-cta">
                        <div className="max-w-2xl">
                            <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                                Watch Jeremy Lipking paint
                            </h2>
                            <p className="text-muted-foreground leading-relaxed mb-8">
                                Browse our workshop catalog to find this and other professional painting demonstrations. Lifetime access, no subscriptions &mdash; study at your own pace.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
                                    <Link to="/workshops">
                                        Browse Workshops <ArrowRight className="ml-2 h-4 w-4" />
                                    </Link>
                                </Button>
                                <Button asChild variant="outline" size="lg">
                                    <Link to="/courses">Browse All Courses</Link>
                                </Button>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default JeremyLipkingPaintingsPage;
